import { Navigate, Outlet, useLocation } from "react-router-dom";

import PageLoader from "@/common/components/PageLoader";
import SessionExpiredOverlay from "@/common/components/SessionExpiredOverlay";
import { useAuth } from "@/common/hooks/useAuth";
import { usePermissions } from "@/common/hooks/usePermissions";

export const TEST_LEAD_ROLE = "Test Lead";

/**
 * Route guard for organisation-level admin pages (e.g. /admin/projects).
 *
 * Authorises any signed-in user whose Membership role is 'admin' or 'owner'
 * (super-admin bypasses the check too). The backend remains the source of
 * truth — protected requests will still 403 if the membership role is
 * insufficient, so this is just a UX hint.
 */
export const OrgAdminRoute = () => {
  const { isAuthenticated, isAppReady, isSuperAdmin } = useAuth();
  const {
    isAdmin: isOrgAdmin,
    roleName,
    isLoading: isPermissionsLoading,
  } = usePermissions();
  const location = useLocation();

  if (!isAppReady) return <PageLoader />;

  if (!isAuthenticated) {
    return (
      <Navigate
        to={`/login?returnUrl=${encodeURIComponent(location.pathname + location.search + location.hash)}`}
        replace
      />
    );
  }

  // Wait for the permissions API to resolve before evaluating access.
  // Without this, a hard reload sends isOrgAdmin=false while the query is
  // still in flight, causing an incorrect redirect to /dashboard.
  if (isPermissionsLoading) return <PageLoader />;

  // isSuperAdmin bypasses the org-admin check; isOrgAdmin comes from the roles
  // permissions API (Membership row) — the backend remains the source of truth.
  // Test Leads get limited access (Users & Roles only — enforced in OrgAdminLayout).
  // Developers are explicitly excluded even if they hold org owner/admin membership.
  const isDeveloper = (roleName ?? "").toLowerCase() === "developer";
  const canAccessOrgAdmin =
    !isDeveloper && (isSuperAdmin || isOrgAdmin || roleName === TEST_LEAD_ROLE);
  if (!canAccessOrgAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      <SessionExpiredOverlay />
      <Outlet />
    </>
  );
};
